// src/features/reports/InsightCallout.jsx
//
// INSIGHT-CALLOUT: Reusable spotlight banner that sits directly under the KPI
// row in report pages. Renders a colored gradient card with a hero metric,
// headline, so-what sentence, a viz panel (splitBar or trend), and inert
// Pin / Dismiss affordances (write-store not yet wired — Phase 2).
//
// Props:
//   type      "pressure" | "growth"
//   metric    string — large hero number/% shown top-left
//   headline  string
//   soWhat    string — explanatory sentence below headline
//   viz       { kind: "splitBar", pressureCount, roomCount }
//           | { kind: "trend", points: number[], startLabel, endLabel }
//   deepLink  string — href for the action anchor
//   action    string — label for the action anchor

import React from "react";
import { T } from "../../app/tokens.js";

// ── Type palette ──────────────────────────────────────────────────────────────
// Gradient strings come from T tokens (tokens.js); all hex is centralised there.

const TYPE_PALETTE = {
  pressure: {
    grad:       T.insightPressureGrad,
    pill:       "Hiring pressure",
    pillBg:     "rgba(255,255,255,0.18)",
    pillColor:  "rgba(255,255,255,0.95)",
    vizStroke:  "rgba(255,255,255,0.55)",
    vizFill:    "rgba(255,255,255,0.25)",
    vizBar:     "rgba(255,255,255,0.85)",
    vizBarRoom: "rgba(255,255,255,0.25)",
  },
  growth: {
    grad:       T.insightGrowthGrad,
    pill:       "HC growth",
    pillBg:     "rgba(255,255,255,0.18)",
    pillColor:  "rgba(255,255,255,0.95)",
    vizStroke:  "rgba(255,255,255,0.55)",
    vizFill:    "rgba(255,255,255,0.2)",
    vizBar:     "rgba(255,255,255,0.85)",
    vizBarRoom: "rgba(255,255,255,0.25)",
  },
};

// ── SplitBar viz ──────────────────────────────────────────────────────────────

function SplitBarViz({ pressureCount, roomCount, palette }) {
  const total = pressureCount + roomCount;
  if (total <= 0) return null;
  const pressurePct = (pressureCount / total) * 100;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 120, maxWidth: 200 }}>
      <div style={{ display: "flex", alignItems: "flex-end", gap: 8 }}>
        <div style={{ fontSize: T.fsLg6, fontWeight: T.fwBold, color: "rgba(255,255,255,0.92)", lineHeight: 1 }}>
          {pressureCount}
        </div>
        <div style={{ fontSize: T.fsXxs, color: "rgba(255,255,255,0.65)", marginBottom: 3, lineHeight: 1.2 }}>
          in pressure<br />markets
        </div>
      </div>

      {/* Split bar */}
      <div style={{ height: 8, borderRadius: 4, background: palette.vizBarRoom, overflow: "hidden", display: "flex" }}>
        <div style={{ width: `${pressurePct}%`, background: palette.vizBar, borderRadius: 4, transition: "width 0.4s ease" }} />
      </div>

      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <span style={{ fontSize: T.fsXxs, color: "rgba(255,255,255,0.65)" }}>
          {pressureCount} at risk
        </span>
        <span style={{ fontSize: T.fsXxs, color: "rgba(255,255,255,0.65)" }}>
          {roomCount} in headroom
        </span>
      </div>
    </div>
  );
}

// ── Trend viz ─────────────────────────────────────────────────────────────────

function TrendViz({ points, startLabel, endLabel, palette }) {
  const W = 160, H = 56, padX = 6, padY = 6;
  if (!Array.isArray(points) || points.length < 2) return null;

  const min = Math.min(...points);
  const max = Math.max(...points);
  const rng = (max - min) || 1;
  const step = (W - 2 * padX) / (points.length - 1);
  const pts = points.map((v, i) => {
    const x = padX + i * step;
    const y = padY + (H - 2 * padY) - ((v - min) / rng) * (H - 2 * padY);
    return [x.toFixed(1), y.toFixed(1)];
  });
  const polyPts = pts.map((p) => p.join(",")).join(" ");

  // Build filled area path
  const areaPath = [
    `M ${pts[0][0]},${H - padY}`,
    ...pts.map(([x, y]) => `L ${x},${y}`),
    `L ${pts[pts.length - 1][0]},${H - padY}`,
    "Z",
  ].join(" ");

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 120, maxWidth: 200 }}>
      <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} style={{ display: "block" }}>
        <path d={areaPath} fill={palette.vizFill} />
        <polyline fill="none" stroke={palette.vizStroke} strokeWidth={1.8} strokeLinejoin="round" points={polyPts} />
        {pts.map(([x, y], i) => (
          <circle key={i} cx={x} cy={y} r={2} fill={palette.vizBar} />
        ))}
      </svg>
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <span style={{ fontSize: T.fsXxs, color: "rgba(255,255,255,0.65)" }}>{startLabel}</span>
        <span style={{ fontSize: T.fsXxs, color: "rgba(255,255,255,0.65)" }}>{endLabel}</span>
      </div>
    </div>
  );
}

// ── CompactInsightStrip ───────────────────────────────────────────────────────

/**
 * CompactInsightStrip — compact white card strip replacing the heavy gradient
 * banner. No gradient, no hero metric, no pill, no Pin/Dismiss.
 *
 * Props:
 *   headline  string — single prose sentence
 *   viz       { kind: "splitBar", pressureCount, roomCount }
 *           | { kind: "trend", points: number[], startLabel, endLabel }
 *           | undefined
 *   deepLink  string — href for optional action link
 *   action    string — label for optional action link
 */
export function CompactInsightStrip({ headline, viz, deepLink, action }) {
  const isSplitBar = viz?.kind === "splitBar";
  const pressureCount = isSplitBar ? (viz.pressureCount ?? 0) : 0;
  const roomCount     = isSplitBar ? (viz.roomCount     ?? 0) : 0;
  const total         = pressureCount + roomCount;
  const showBar       = isSplitBar && total > 0;
  const pressurePct   = showBar ? (pressureCount / total) * 100 : 0;

  return (
    <div
      data-testid="insight-strip"
      style={{
        background:   T.white,
        border:       `1px solid ${T.border}`,
        borderRadius: 8,
        padding:      "12px 16px",
        marginBottom: 14,
      }}
    >
      {/* Headline */}
      <div style={{
        fontSize:     T.fsBase,
        fontWeight:   T.fwSemibold,
        color:        T.dark,
        lineHeight:   1.5,
        marginBottom: showBar ? 10 : (action && deepLink ? 8 : 0),
      }}>
        {headline}
      </div>

      {/* Split bar */}
      {showBar && (
        <>
          <div style={{
            height:       8,
            borderRadius: 4,
            background:   T.border,
            overflow:     "hidden",
            display:      "flex",
            marginBottom: 6,
          }}>
            <div style={{
              width:        `${pressurePct}%`,
              background:   T.red,
              borderRadius: 4,
              transition:   "width 0.4s ease",
            }} />
          </div>

          {/* Two-dot legend */}
          <div style={{
            display:        "flex",
            justifyContent: "space-between",
            marginBottom:   action && deepLink ? 8 : 0,
          }}>
            <span
              data-testid="insight-strip-legend-pressure"
              style={{ fontSize: T.fsXxs, color: T.mid }}
            >
              ● {pressureCount} into capacity pressure
            </span>
            <span
              data-testid="insight-strip-legend-room"
              style={{ fontSize: T.fsXxs, color: T.mid }}
            >
              {roomCount} into markets with room ●
            </span>
          </div>
        </>
      )}

      {/* Optional action link */}
      {action && deepLink && (
        <div>
          <a
            href={deepLink}
            data-testid="insight-strip-action"
            style={{
              fontSize:       T.fsSm5,
              color:          T.mid,
              textDecoration: "none",
            }}
          >
            {action} →
          </a>
        </div>
      )}
    </div>
  );
}

// ── Main export ───────────────────────────────────────────────────────────────

/**
 * InsightCallout — full-width spotlight banner for report pages.
 *
 * @param {{
 *   type: "pressure"|"growth",
 *   metric: string,
 *   headline: string,
 *   soWhat: string,
 *   viz: object,
 *   deepLink: string,
 *   action: string,
 * }} props
 */
export function InsightCallout({ type, metric, headline, soWhat, viz, deepLink, action }) {
  const palette = TYPE_PALETTE[type] ?? TYPE_PALETTE.pressure;

  const VizPanel = viz?.kind === "splitBar" ? (
    <SplitBarViz
      pressureCount={viz.pressureCount ?? 0}
      roomCount={viz.roomCount ?? 0}
      palette={palette}
    />
  ) : viz?.kind === "trend" ? (
    <TrendViz
      points={viz.points ?? []}
      startLabel={viz.startLabel ?? ""}
      endLabel={viz.endLabel ?? ""}
      palette={palette}
    />
  ) : null;

  return (
    <div
      data-testid="insight-callout"
      data-insight-type={type}
      style={{
        background:   palette.grad,
        borderRadius: 12,
        padding:      "18px 24px",
        marginBottom: 14,
        display:      "flex",
        alignItems:   "flex-start",
        gap:          24,
        boxShadow:    "0 4px 18px rgba(0,0,0,0.18)",
        flexWrap:     "wrap",
      }}
    >
      {/* Left: eyebrow + metric + copy */}
      <div style={{ flex: "1 1 200px", minWidth: 0 }}>
        {/* Eyebrow row: "Insight" label + type pill */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
          <span style={{ fontSize: T.fsXxs, fontWeight: T.fwBold, letterSpacing: "0.07em", textTransform: "uppercase", color: "rgba(255,255,255,0.65)" }}>
            Insight
          </span>
          <span style={{
            display:     "inline-flex",
            alignItems:  "center",
            fontSize:    T.fsXs,
            fontWeight:  T.fwSemibold,
            padding:     "2px 10px",
            borderRadius: 6,
            background:  palette.pillBg,
            color:       palette.pillColor,
          }}>
            {palette.pill}
          </span>
        </div>

        {/* Hero metric */}
        <div
          data-testid="insight-callout-metric"
          style={{ fontSize: T.fs3xl, fontWeight: T.fwBold, color: "rgba(255,255,255,0.96)", lineHeight: 1.1, marginBottom: 6 }}
        >
          {metric}
        </div>

        {/* Headline */}
        <div style={{ fontSize: T.fsMd5, fontWeight: T.fwSemibold, color: "rgba(255,255,255,0.92)", marginBottom: 6 }}>
          {headline}
        </div>

        {/* So-what */}
        <div style={{ fontSize: T.fsBase, color: "rgba(255,255,255,0.72)", lineHeight: 1.5, marginBottom: 14, maxWidth: 520 }}>
          {soWhat}
        </div>

        {/* Action row */}
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <a
            href={deepLink}
            data-testid="insight-callout-action"
            style={{
              fontSize:       T.fsSm5,
              fontWeight:     T.fwBold,
              color:          "rgba(255,255,255,0.95)",
              textDecoration: "underline",
              textUnderlineOffset: 3,
            }}
          >
            {action} →
          </a>

          {/* Pin — visually present but inert until write-store is wired (Phase 2) */}
          <button
            type="button"
            data-testid="insight-callout-pin"
            title="Pin to In Focus (coming soon)"
            style={{
              background:    "none",
              border:        "none",
              cursor:        "default",
              padding:       "2px 6px",
              fontSize:      T.fsXxs,
              color:         "rgba(255,255,255,0.85)",
              fontWeight:    T.fwSemibold,
              borderRadius:  6,
              /* Phase-2 gate: write store not yet wired */
              opacity:       0.45,
              pointerEvents: "none",
            }}
          >
            📌 Pin
          </button>

          {/* Dismiss — visually present but inert until write-store is wired (Phase 2) */}
          <button
            type="button"
            data-testid="insight-callout-dismiss"
            title="Dismiss (coming soon)"
            style={{
              background:    "none",
              border:        "none",
              cursor:        "default",
              padding:       "2px 6px",
              fontSize:      T.fsXxs,
              color:         "rgba(255,255,255,0.85)",
              fontWeight:    T.fwSemibold,
              borderRadius:  6,
              /* Phase-2 gate: write store not yet wired */
              opacity:       0.45,
              pointerEvents: "none",
            }}
          >
            ✕ Dismiss
          </button>
        </div>
      </div>

      {/* Right: viz panel */}
      {VizPanel && (
        <div style={{ flexShrink: 0, display: "flex", alignItems: "center" }}>
          {VizPanel}
        </div>
      )}
    </div>
  );
}
